import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { OptimizePlanConfig } from '../../../../config/optimizePlanConfig';
import type { FormData, Level } from '../../components/OptimizePlan/types';
import { INITIAL_FORM_DATA } from '../../components/OptimizePlan/types';
import { buildScopeFilters } from '../../utils/optimizePayloadBuilder';
import type { ScopeSelection } from '../../utils/optimizePayloadBuilder';

dayjs.extend(utc);

interface UseOptimizePlanFormParams {
    form: FormInstance;
    planId: string;
    config: OptimizePlanConfig;
}

export function useOptimizePlanForm({ form, planId, config }: UseOptimizePlanFormParams) {
    const topLevelKey =
        config.constraintLevelOptions.find((o) => o.depthLevel === 0)?.key ??
        config.constraintLevelOptions[0]?.key ??
        'category';

    // Build a sorted list of (order, fieldName, levelKey, attributeType) pairs, keyed by depthLevel.
    const depthLevelPairs = useMemo(() => {
        const levelByDepth = new Map(
            config.constraintLevelOptions.filter((o) => o.depthLevel > 0).map((o) => [o.depthLevel, o.key]),
        );
        return config.scopeOfOptimisationFields
            .filter((f) => f.name != null && f.depthLevel != null)
            .map((f) => ({
                order: f.depthLevel!,
                fieldName: f.name!,
                attributeType: f.attributeType,
                levelKey: levelByDepth.get(f.depthLevel!) ?? topLevelKey,
            }))
            .sort((a, b) => a.order - b.order);
    }, [config.constraintLevelOptions, config.scopeOfOptimisationFields, topLevelKey]);

    const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
    const [level, setLevel] = useState<Level>(topLevelKey);

    const allFormValues = Form.useWatch([], form) as Record<string, unknown> | undefined;

    const scopeSelections: ScopeSelection[] = useMemo(
        () =>
            depthLevelPairs.map(({ fieldName, attributeType }) => {
                const val = allFormValues?.[fieldName];
                return { attributeType, values: Array.isArray(val) ? (val as string[]) : [] };
            }),
        [allFormValues, depthLevelPairs.map((p) => p.fieldName).join(',')],
    );

    const scopeFilters = useMemo(() => buildScopeFilters(scopeSelections), [scopeSelections]);

    // Derive the deepest pair index during render so all consumers in this render
    // cycle agree — avoids a one-render lag if level were synced only via useEffect.
    const deepestSelectedPairIdx = useMemo(() => {
        const rev = [...scopeSelections].reverse().findIndex((s) => s.values.length > 0);
        return rev >= 0 ? scopeSelections.length - 1 - rev : -1;
    }, [scopeSelections]);

    // The constraint level that corresponds to the deepest scope selection.
    const derivedLevel: Level =
        deepestSelectedPairIdx >= 0 ? depthLevelPairs[deepestSelectedPairIdx]?.levelKey ?? topLevelKey : topLevelKey;

    const promotionCountStartDate =
        formData.optimization_start_date != null
            ? dayjs.unix(formData.optimization_start_date).utc().startOf('day').unix()
            : null;

    const requiredFormDataPayload = useMemo(() => {
        if (!formData.optimization_start_date || !formData.objective) return null;

        const attribute_list = deepestSelectedPairIdx >= 0 ? scopeSelections[deepestSelectedPairIdx].values : [];

        return {
            objective: formData.objective,
            start_date: formData.optimization_start_date,
            plan_id: planId,
            is_tpm_plan: false,
            // Use derivedLevel so payload and level are always in sync within the same render.
            level: derivedLevel,
            attribute_list,
        };
    }, [
        formData.objective,
        formData.optimization_start_date,
        derivedLevel,
        deepestSelectedPairIdx,
        scopeSelections,
        planId,
    ]);

    const levelOptions = useMemo(
        () =>
            config.constraintLevelOptions.map((opt) => ({
                ...opt,
                disabled:
                    opt.disableAtDepthLevel != null
                        ? scopeSelections.some(
                              (s, i) => depthLevelPairs[i]?.order >= opt.disableAtDepthLevel! && s.values.length > 0,
                          )
                        : false,
            })),
        [scopeSelections, config.constraintLevelOptions, depthLevelPairs],
    );

    useEffect(() => {
        setLevel(derivedLevel);
    }, [derivedLevel]);

    const resetForm = useCallback(() => {
        setFormData(INITIAL_FORM_DATA);
        form.resetFields();
        setLevel(topLevelKey);
    }, [form, topLevelKey]);

    return {
        formData,
        setFormData,
        level,
        setLevel,
        levelOptions,
        scopeSelections,
        scopeFilters,
        promotionCountStartDate,
        requiredFormDataPayload,
        resetForm,
    };
}
